import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import VMasker from 'vanilla-masker';
import { toast } from 'react-toastify';

import Grid from '@material-ui/core/Grid';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import Container from '@material-ui/core/Container';
import Button from '../../../components/Button';
import { Formik, Form } from 'formik';

import TermsOfUseModal from 'pages/ProvisionalReceiptConfig/components/TermsOfUseModal';

import { Certificate, BasicInfo, Filter } from './steps';

import { certificateValidations } from './steps/Certificate/validations';
import { basicInfoValidations } from './steps/BasicInfo/validations';

import { setBasicInfo, setCertificate } from '../store/actions';

import { loadTermsOfUseUsers, loadTermsOfUseByEstablishment } from '../../ProvisionalReceiptConfig/store/thunk';

import {
  loadProvisionalReceiptProductConfig,
  loadProvisionalReceiptProductTables,
  uploadCertificate,
  upsertNfe,
  setAutomaticSendFilters
} from '../store/thunk';

import { loadFilters } from 'components/Filter/store/thunk';

import { checkedValueByType } from 'helpers/common';
import { formatToOnlyNumbersAndLetters } from 'helpers/formatters';

import { useStyles } from './styles';

export default function ProvisionalReceiptProductConfig() {
  const dispatch = useDispatch();

  const history = useHistory();

  const classes = useStyles();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isAdminUser } = useSelector(state => state.profile.submit);
  const { termsOfUseUsers, acceptanceDateTime } = useSelector(state => state.provisionalReceiptConfig);

  const {
    isLoading,
    doesConfigExists,
    configType,
    basicInfo,
    certificate,
    certificateFile,
    certificateFileFromAPI,
    filter
  } = useSelector(state => state.provisionalReceiptProductConfig);

  const isUserAdmin = +isAdminUser == 1;

  const isNfTermsAccepted = termsOfUseUsers
    ?.some(user => user?.acceptanceDateTime != null && user?.acceptanceDateTime != '' && user?.acceptanceDateTime != '0001-01-01 00:00:00');

  const [isTermsOfUseModalOpen, setIsTermsOfUseModalOpen] = useState(false);
  const [isTermsOfUseModalAutoDismissed, setIsTermsOfUseModalAutoDismissed] = useState(false);
  const [isTermsLoaded, setIsTermsLoaded] = useState(!isUserAdmin);

  const [currentIndex, setCurrentIndex] = useState(0);

  const configurations = [
    {
      index: 0,
      id: 'certificate',
      title: 'Certificado Digital',
      component: Certificate,
      validations: certificateValidations
    },
    {
      index: 1,
      id: 'basic',
      title: 'Informações básicas',
      component: BasicInfo,
      validations: basicInfoValidations
    },
    {
      index: 2,
      id: 'filter',
      title: 'Filtro de envio',
      component: Filter,
      validations: null
    }
  ];

  const currentConfiguration = configurations.find(config => config.index == currentIndex) || {};

  useEffect(() => {
    if(establishmentId) {
      setIsTermsLoaded(false);
      setIsTermsOfUseModalOpen(false);
      setIsTermsOfUseModalAutoDismissed(false);

      dispatch(loadProvisionalReceiptProductTables(userId));
      dispatch(loadProvisionalReceiptProductConfig(userId, establishmentId));
      dispatch(loadFilters(userId, establishmentId));

      if(!isUserAdmin) {
        dispatch(loadTermsOfUseByEstablishment(userId, establishmentId)).then(() => {
          setIsTermsLoaded(true);
        });
      }
    }
  }, [establishmentId, isUserAdmin]);

  useEffect(() => {
    if(isUserAdmin) {
      if(establishmentId) {
        dispatch(loadTermsOfUseUsers(userId, establishmentId))
          .then(() => setIsTermsLoaded(true));
      }
    }
  }, [establishmentId]);

  const isAutoTermsOfUseModalOpen = isTermsLoaded && !isTermsOfUseModalAutoDismissed && (!isUserAdmin && acceptanceDateTime != null && !acceptanceDateTime);

  const handleTermsOfUseModalOpen = () => {
    setIsTermsOfUseModalOpen(true);
  }

  const handleTermsOfUseModalState = isOpen => {
    if(isOpen) {
      setIsTermsOfUseModalOpen(true);
      return;
    }

    if(isTermsOfUseModalOpen) {
      setIsTermsOfUseModalOpen(false);
      return;
    }

    if(isUserAdmin) {
      setIsTermsOfUseModalAutoDismissed(true);
    }
  }

  const handlePreviousStep = () => {
    if(currentIndex == 0) {
      return history.push('/notas-fiscais');
    }

    setCurrentIndex(currentIndex - 1);
  }

  const handleNextStep = () => {
    if(currentIndex == configurations[configurations.length - 1].index) {
      return;
    }

    setCurrentIndex(currentIndex + 1);
  }

  const handleSubmit = params => {
    if(currentConfiguration.id == 'certificate') {
      if(params?.certificateName && !params?.certificateFile) {
        return handleNextStep();
      }

      if(!certificateFileFromAPI) {
        if(!params?.certificateFile) {
          return toast.error('Escolha um certificado para continuar.');
        }
      }

      if(params?.certificateFile) {
        if(!params?.password && !params?.passwordVerify) {
          return handleNextStep();
        }

        const values = {
          certificateFile: params?.certificateFile,
          autenticatorPassword: params?.password,
          autenticatorPasswordConfirm: params?.passwordVerify
        }

        dispatch(uploadCertificate(userId, establishmentId, values))
          .then(data => {
            dispatch(setCertificate(data));
            handleNextStep();
          });
      }
    }

    if(currentConfiguration.id == 'basic') {
      if(+configType == 0) {
        return handleNextStep();
      }

      const basicInfoParams = {
        ...params,
        ...(basicInfo?.certificateId != certificate?.certificateId) && certificate,
        taxSettingsUuid: basicInfo?.uuid || '',
        nfce: {
          tokenCsc: params?.nfce?.tokenCsc,
          IdTokenCsc: params?.nfce?.IdTokenCsc,
          pis_aliquot: params?.nfce?.pis_aliquot,
          pis_cst: params?.nfce?.pis_cst,
          cofins_aliquot: params?.nfce?.cofins_aliquot,
          cofins_cst: params?.nfce?.cofins_cst
        }
      }

      basicInfoParams['documentId'] = formatToOnlyNumbersAndLetters(params?.documentId);
      basicInfoParams['postalCode'] = VMasker.toNumber(params?.postalCode);

      delete basicInfoParams['taxSettingsUuid'];
      delete basicInfoParams['uuid'];

      dispatch(setBasicInfo(params));

      dispatch(upsertNfe(userId, establishmentId, basicInfoParams, doesConfigExists))
        .then(() => handleNextStep());
    }

    if(currentConfiguration.id == 'filter') {
      const {
        nfeStatus,
        orders,
        selectedTransactions,
        paymentMethods
      } = params;

      const filterParams = {
        nfeStatus,
        taxSettingsUuid: basicInfo?.uuid,
        automaticGenerateNfe: JSON.stringify({
          orders: checkedValueByType(orders, 'number'),
          selectedTransactions: checkedValueByType(selectedTransactions, 'number'),
          paymentMethods
        })
      }

      dispatch(setAutomaticSendFilters(userId, establishmentId, filterParams))
        .then(() => history.push('/notas-fiscais'));
    }
  }

  const getInitialValues = () => {
    if(currentConfiguration.id == 'certificate') {
      return {
        ...certificate,
        certificateFile,
        certificate: '',
        password: '',
        passwordVerify: ''
      }
    }

    if(currentConfiguration.id == 'basic') {
      return basicInfo;
    }

    if(currentConfiguration.id == 'filter') {
      return filter;
    }

    return {}
  }

  return(
    <Grid>
      {(isTermsOfUseModalOpen || isAutoTermsOfUseModalOpen) && (
        <TermsOfUseModal
          isDismissable={isUserAdmin || isTermsOfUseModalOpen}
          isTermsAccepted={isUserAdmin ? isNfTermsAccepted : !!acceptanceDateTime}
          isOpen={isTermsOfUseModalOpen || isAutoTermsOfUseModalOpen}
          setIsOpen={handleTermsOfUseModalState}
        />
      )}
      <Stepper
        alternativeLabel
        activeStep={currentIndex}
        style={{ background: 'transparent', padding: 0, paddingBottom: 24 }}
      >
        {configurations.map((nav, index) => (
          <Step key={nav.id}>
            <StepButton
              disabled={!doesConfigExists}
              completed={currentIndex > index}
              onClick={() => setCurrentIndex(index)}
            >
              {nav.title}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Grid>
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={currentConfiguration.validations}
          validateOnChange={false}
          onSubmit={handleSubmit}
        >
          {({ ...formikProps }) => {
            return(
              <Form>
                {(currentIndex == currentConfiguration.index) && (
                  <currentConfiguration.component
                    formikProps={formikProps}
                    onOpenTermsOfUseModal={handleTermsOfUseModalOpen}
                  />
                )}
                <Grid className={classes.footer}>
                  <Container>
                    <Grid container justify="space-between">
                      <Button
                        loading={isLoading}
                        color="secondary"
                        type="button"
                        onClick={handlePreviousStep}
                      >
                        Voltar
                      </Button>
                      <Button
                        loading={isLoading}
                        color="success"
                        type="submit"
                      >
                        Avançar
                      </Button>
                    </Grid>
                  </Container>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
}